import { useStore } from '../state/store';
import { getCommand, runCommand, isEnabled, formatShortcut } from '../commands/registry';
import { effectiveShortcuts } from '../input/keymap';
import { Icon } from './common';
import { beginNoteDrag, consumeDragClick, type NoteDragPayload } from './noteDrag';
import { tip, type TipSide } from './Tooltip';

export function shortcutFor(id: string): string | undefined {
  const prefs = useStore.getState().editorPrefs;
  const keys = effectiveShortcuts(prefs).get(id);
  return keys?.[0] ? formatShortcut(keys[0]) : undefined;
}

/** Tooltip props for a command: its name, shortcut and hint. */
export function commandTip(id: string, opts: { hint?: string; side?: TipSide } = {}) {
  const c = getCommand(id);
  return tip(c?.label ?? id, { keys: shortcutFor(id), hint: opts.hint ?? c?.hint, side: opts.side });
}

/**
 * A button rendered from the command registry. Subscribes to the state it needs
 * for active/enabled. With `drag`, it can also be dragged onto the score to
 * place that note value (or a rest) where it is dropped.
 */
export function CommandButton({ id, size = 22, showLabel = false, className = '', drag, tipSide }: { id: string; size?: number; showLabel?: boolean; className?: string; drag?: NoteDragPayload; tipSide?: TipSide }) {
  // Re-render on editor/mode changes (active and enabled states depend on them).
  const s = useStore((st) => st);
  const c = getCommand(id);
  if (!c) return null;
  const ctx = { s, ed: s.ed };
  const active = c.isActive?.(ctx) ?? false;
  const enabled = isEnabled(c, ctx);
  return (
    <button
      type="button"
      className={`cmd${active ? ' active' : ''}${showLabel ? ' with-label' : ''}${drag ? ' draggable-note' : ''} ${className}`}
      aria-label={c.label}
      aria-pressed={c.isActive ? active : undefined}
      {...commandTip(id, { side: tipSide })}
      disabled={!enabled}
      data-cmd={id}
      onMouseDown={(e) => e.preventDefault()}
      onPointerDown={drag ? (e) => beginNoteDrag(e, drag) : undefined}
      onClick={(e) => {
        if (drag && consumeDragClick(e.currentTarget)) return;
        runCommand(id, undefined, { recent: true });
      }}
    >
      <Icon icon={c.icon} size={size} />
      {showLabel && <span className="cmd-label">{c.label}</span>}
    </button>
  );
}
